import { useState, useRef, useEffect } from "react";
import './EmailConfirmationForm.scss';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../../api/constants'
import { requestInstance } from '../../../services/AuthService/AccessTokenUpdateService';
import { saveTokens } from '../../../services/TokenService/tokens'
import { useUser } from '../../../context/UserContext';

export const EmailConfirmationForm = ({ setIsEmailConfirmationFormVisible }) => {
  const [code, setCode] = useState(new Array(6).fill(""));
  const [errors, setErrors] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { setUser } = useUser();
  const navigate = useNavigate();
  const formRef = useRef(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setIsEmailConfirmationFormVisible(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsEmailConfirmationFormVisible]);

  const handleChange = (element, index) => {
    setErrors('');
    if (isNaN(element.value)) return;

    setCode([...code.map((d, idx) => (idx === index ? element.value : d))]);

    if (element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handlePaste = (event) => {
    const paste = event.clipboardData.getData("text");
    if (paste.length === 6 && !isNaN(paste)) {
      const newCode = paste.split("");
      setCode(newCode);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setIsSubmitting(true);
      const email = localStorage.getItem('email');
      if (!email) {
        setErrors('Email not found. Please restart registration.');
        setIsSubmitting(false);
        return;
      }
      
      const url = BASE_URL + 'auth/resend-confirmation-code/';
      await requestInstance.post(url, { email });
      
      // Start cooldown timer (60 seconds)
      setResendCooldown(60);
      const timer = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setErrors('');
      setIsSubmitting(false);
    } catch (error) {
      console.error('Failed to resend code:', error);
      setErrors(error.response?.data?.detail || 'Failed to resend verification code.');
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate the code is complete
    if (code.some(digit => digit === '')) {
      setErrors('Please enter a complete 6-digit code');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const url = BASE_URL + 'auth/registration-3-step/';
      const email = localStorage.getItem('email');
      const companyID = localStorage.getItem('companyID');
      
      if (!email || !companyID) {
        setErrors('Registration information missing. Please restart registration.');
        setIsSubmitting(false);
        return;
      }
      
      const result = await requestInstance.post(url, {
        email: email,
        code: code.join(""),
        company_id: companyID
      });
      
      if (result?.data) {
        // Save authentication tokens
        saveTokens(result.data);
        setIsEmailConfirmationFormVisible(false);
        
        // Fetch user data
        const userUrl = BASE_URL + 'auth/me/';
        const response = await requestInstance.get(userUrl);
        const userData = response.data;
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        // Check for selected product in localStorage
        const selectedProduct = localStorage.getItem('selectedProduct');
        if (selectedProduct) {
          try {
            const product = JSON.parse(selectedProduct);
            localStorage.removeItem('selectedProduct'); // Clear it after use
            navigate(`/products/${product.id}`);
            return; // Exit early to prevent default navigation
          } catch (error) {
            console.error('Error parsing selected product:', error);
            // Continue with normal flow if product parsing fails
          }
        }

        // Default navigation
        navigate('/subscriptions');
      }
    } catch (error) {
      console.error('Verification failed:', error);
      
      // Handle different error responses
      if (error.response) {
        if (error.response.status === 404) {
          setErrors('Verification service is currently unavailable. Please try again later.');
        } else if (error.response.data?.code) {
          setErrors(error.response.data.code);
        } else if (error.response.data?.detail) {
          setErrors(error.response.data.detail);
        } else if (typeof error.response.data === 'string') {
          setErrors(error.response.data);
        } else {
          setErrors('Verification failed. Please check your code and try again.');
        }
      } else if (error.request) {
        setErrors('Network error. Please check your connection and try again.');
      } else {
        setErrors('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="LoginForm" ref={formRef}>
      <div className="LoginForm__header">
        <h2 className="LoginForm__title">Confirm email</h2>
        <button 
          className="LoginForm__close-button" 
          onClick={() => setIsEmailConfirmationFormVisible(false)}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <form className="LoginForm__form" method='post' onSubmit={handleSubmit}>
        <div className="EmailConfirmationForm__input-group">
          {code.map((data, index) => (
            <input
              key={index}
              type="text"
              name="code"
              maxLength="1"
              className="EmailConfirmationForm__input"
              value={data}
              onChange={e => handleChange(e.target, index)}
              onFocus={e => e.target.select()}
              onPaste={handlePaste}
              disabled={isSubmitting}
            />
          ))}
        </div>
        
        {errors && <p style={{textAlign: 'center', color: '#e53935'}} className="ErrorText">{errors}</p>}
        
        <button type="submit" className="LoginForm__submit" disabled={isSubmitting}>
          {isSubmitting ? 'Verifying...' : 'Submit'}
        </button>
        
        <div style={{marginTop: '15px', textAlign: 'center'}}>
          <p style={{fontSize: '14px', color: '#555'}}>
            Didn't receive a code?
          </p>
          <button 
            type="button" 
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              color: resendCooldown > 0 ? '#999' : '#1976d2',
              textDecoration: 'underline',
              cursor: resendCooldown > 0 ? 'default' : 'pointer',
              fontSize: '14px',
              marginTop: '5px'
            }}
          >
            {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend verification code'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailConfirmationForm;
